<template>
  <div>
    <div v-if="canManage" class="mb-3.5 flex flex-col items-end">
      <button
        type="button"
        class="flex cursor-pointer items-center gap-2 text-gray-400 hover:text-gray-500"
        @click="toggleHidden"
      >
        <EyeOpenIcon class="size-[18px]" />
        <span class="font-medium text-[14px] leading-[22px]">
          {{ isHidden ? 'Show match' : 'Hide match' }}
        </span>
      </button>

      <span
        v-if="isHidden"
        class="mt-1 text-[14px] leading-[22px] text-gray-400"
      >
        This match will not appear on a downloaded or shared report
      </span>
    </div>

    <div
      class="grid grid-cols-2 gap-x-1 min-h-20"
      :class="{ 'opacity-[0.36]': collapsed }"
    >
      <div
        class="h-full rounded-t-xl px-2.5 pt-2.5 pb-2 relative"
        :class="{
          'bg-gray-300/30': !isRegisteredTabActive,
          'bg-gray-400': isRegisteredTabActive,
          'rounded-b-xl': collapsed,
          'after:absolute after:inset-x-0 after:top-full after:h-3 after:bg-inherit':
            !collapsed,
        }"
        @click="setActiveTab('registered')"
      >
        <div
          class="font-medium text-[12px] leading-[18px] px-[5px] py-px rounded-md w-fit mb-[3px]"
          :class="{
            'opacity-60 bg-gray-200 text-gray-800': !isRegisteredTabActive,
            'opacity-full bg-gray-500 text-white': isRegisteredTabActive,
          }"
        >
          Registered work
        </div>

        <div
          class="pl-1 text-[13px] leading-[18px]"
          :class="{
            'opacity-60 text-gray-800': !isRegisteredTabActive,
            'opacity-full text-white': isRegisteredTabActive,
          }"
        >
          {{ match.copyright.location }}
        </div>
      </div>

      <div
        class="h-full rounded-t-xl px-2.5 pt-2.5 pb-2 flex flex-col relative"
        :class="[
          backgroundClasses,
          {
            'rounded-b-xl': collapsed,
            'after:absolute after:inset-x-0 after:top-full after:h-3 after:bg-inherit':
              !collapsed,
          },
        ]"
        @click="setActiveTab('infringing')"
      >
        <div
          class="font-medium text-[12px] leading-[18px] px-[5px] py-px text-white rounded-md w-fit mb-[3px]"
          :class="pillClasses"
        >
          <span class="tablet-h:hidden">Match</span>
          <span class="hidden tablet-h:block">Potential infringement</span>
          {{ formatNumber(number) }}
        </div>

        <div class="pl-1 text-[13px] leading-[18px] text-white">
          {{ match.infringingFile.location }}
        </div>

        <div class="flex items-center gap-x-3 mt-1.5 pl-1">
          <div
            class="font-medium text-[12px] leading-[18px] text-white whitespace-nowrap"
          >
            {{ formatNumber(match.infringingFile.score) }}% match
          </div>

          <div class="w-[71px] h-2 bg-[#f5f8fc] rounded-md p-px">
            <div
              class="h-full rounded-md"
              :class="backgroundClasses"
              :style="{
                width: `${Number(match.infringingFile.score)}%`,
              }"
            ></div>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="!collapsed"
      class="relative border-2 rounded-b-xl bg-white px-[14px] pt-[22px] pb-[14px] flex flex-col gap-y-4"
      :class="[
        classes,
        isRegisteredTabActive ? 'rounded-tr-xl' : 'rounded-tl-xl',
      ]"
    >
      <div v-if="isRegisteredTabActive">
        <div class="text-[12px] leading-[16px] text-gray-500 mb-0.5">
          Registration
        </div>

        <div class="flex gap-x-1.5">
          <div class="w-5 h-5 rounded-xs bg-gray-200/25 p-px shrink-0">
            <Thumbnail
              :type="fileTypeToThumbnail(copyrightFile.type)"
              :src="copyrightPreviewImageUrl"
            />
          </div>

          <div>
            <div
              class="font-medium text-[13px] leading-[20px] text-gray-800 mb-[3px]"
            >
              {{ copyrightFile.name }}
            </div>

            <div
              v-if="showOriginalFilename"
              class="text-[12px] leading-[18px] text-gray-800 mb-[3px]"
            >
              Filename:
              {{ copyrightFile.metadata.originalFilename }}
            </div>

            <RouterLink
              :to="`/file/${copyrightFile.copyright.id}`"
              class="text-[12px] leading-[18px] text-link"
            >
              View registration
            </RouterLink>
          </div>
        </div>
      </div>

      <div v-if="isInfringingTabActive" class="flex gap-x-3">
        <AlertIcon
          class="mt-0.5 size-[26px] shrink-0"
          :class="{
            'text-red-500': match.infringingFile.level === ScoreLevel.High,
            'text-yellow-500': match.infringingFile.level === ScoreLevel.Medium,
            'text-green-500': match.infringingFile.level === ScoreLevel.Low,
          }"
        />
        <div class="font-medium text-[24px] leading-[30px]">
          {{ match.infringingFile.title }}
        </div>
      </div>

      <div v-if="isInfringingTabActive">
        <div class="text-[12px] leading-[16px] text-gray-500 mb-0.5">
          Potential infringing work
        </div>

        <div class="flex items-center gap-x-1.5">
          <div class="w-5 h-5 rounded-xs bg-gray-200/25 p-px shrink-0">
            <Thumbnail
              :type="fileTypeToThumbnail(infringingFile.type)"
              :src="infringingFile.asset.previewImageUrl"
            />
          </div>
          <div class="text-[12px] leading-[18px] text-gray-800">
            {{ infringingFile.name }}
          </div>
        </div>
      </div>

      <template v-if="isInfringingTabActive">
        <div>
          <div class="text-[12px] leading-[16px] text-gray-500 mb-1.5">
            Infringement findings
          </div>

          <div class="text-[14px] leading-[20px]">
            {{ match.infringingFile.summary }}
          </div>
        </div>

        <div class="w-full h-px bg-gray-200"></div>
      </template>

      <component
        :key="activeTab"
        :is="component"
        :match="matchData"
        :number="number"
        :side="activeTab"
        :allow-playback="canPlayMatches"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  type FragmentType,
  useFragment,
} from '@/shared/graphql/router/fragment-masking'
import {
  MatchCategoryType,
  ScoreLevel,
  type File,
  type InfringingFile,
} from '@/shared/graphql/router/graphql'
import { computed, ref, watch, type Component } from 'vue'
import AudioMatchGroup from './AudioMatchGroup.vue'
import ImageMatch from './ImageMatch.vue'
import { MatchBreakdownFragment } from './Match.vue'
import TextMatchGroup from './TextMatchGroup.vue'
import TranscriptMatch from './TranscriptMatch.vue'
import VideoMatch from './VideoMatch.vue'
import { Thumbnail } from '@dacr/library'
import { AlertIcon, EyeOpenIcon } from '@dacr/icons'
import { fileTypeToThumbnail } from '@/shared/utils/thumbnail'
import { formatNumber } from '@/shared/utils/number'
import { applyAuthorizationToken } from '@/shared/utils/url'
import { useMatchVisibility } from '@/features/infringement/composables/useMatchVisibility'

const props = defineProps<{
  type: MatchCategoryType
  match: FragmentType<typeof MatchBreakdownFragment>
  number: number
  allowPlayback?: boolean
  reportId?: string
  canManage?: boolean
}>()

const match = useFragment(MatchBreakdownFragment, props.match)

const canPlayMatches = computed(() => props.allowPlayback !== false)

const { setHidden } = useMatchVisibility()

const isHidden = ref(match.infringingFile.hidden ?? false)

watch(
  () => match.infringingFile.hidden,
  (value) => (isHidden.value = value ?? false)
)

const toggleHidden = async () => {
  const reportId = props.reportId
  if (!reportId) return
  const next = !isHidden.value
  isHidden.value = next
  const ok = await setHidden(reportId, match.infringingFile.id, next)
  if (!ok) isHidden.value = !next
}

const collapsed = computed(() => isHidden.value && !!props.canManage)

const activeTab = ref<'registered' | 'infringing'>('infringing')

const isRegisteredTabActive = computed(() => activeTab.value === 'registered')
const isInfringingTabActive = computed(() => activeTab.value === 'infringing')

const copyrightFile = computed(() => match.copyright.file as File)
const infringingFile = computed(
  () => match.infringingFile.file as InfringingFile
)
const copyrightPreviewImageUrl = computed(() =>
  applyAuthorizationToken(copyrightFile.value.asset?.previewImageUrl)
)

const showOriginalFilename = computed(
  () =>
    copyrightFile.value.name !== copyrightFile.value.metadata.originalFilename
)

const classes = computed(() => ({
  'border-gray-400': isRegisteredTabActive.value,
  ...(isInfringingTabActive.value && {
    'border-red-500': match.infringingFile.level === ScoreLevel.High,
    'border-yellow-500': match.infringingFile.level === ScoreLevel.Medium,
    'border-green-500': match.infringingFile.level === ScoreLevel.Low,
  }),
}))

const setActiveTab = (tab: 'registered' | 'infringing') => {
  activeTab.value = tab
}

const component = computed<Component>(() => {
  if (props.type === MatchCategoryType.Audio) {
    return AudioMatchGroup
  }

  if (props.type === MatchCategoryType.Text) {
    return TextMatchGroup
  }

  if (props.type === MatchCategoryType.Transcript) {
    return TranscriptMatch
  }

  if (props.type === MatchCategoryType.Video) {
    return VideoMatch
  }

  return ImageMatch
})

const pillClasses = computed(() => ({
  'bg-red-600': match.infringingFile.level === ScoreLevel.High,
  'bg-yellow-600': match.infringingFile.level === ScoreLevel.Medium,
  'bg-green-600': match.infringingFile.level === ScoreLevel.Low,
}))

const backgroundClasses = computed(() => ({
  'opacity-40': isRegisteredTabActive.value,
  'bg-red-500': match.infringingFile.level === ScoreLevel.High,
  'bg-yellow-500': match.infringingFile.level === ScoreLevel.Medium,
  'bg-green-500': match.infringingFile.level === ScoreLevel.Low,
}))

const matchData = computed(() => {
  return activeTab.value === 'registered'
    ? match.copyright
    : match.infringingFile
})
</script>
